import { Component, forwardRef, signal, ViewChild, ViewContainerRef } from '@angular/core';
import { LMBaseComponent } from '@base/lm-base.component';
import {
    LMBusyIndicatorService,
    LMComponentInfoService, LMContextService,
    LMErrorUtilService,
    LMModalOpenService,
    LMModalWrapperService,
    LMRouteComponentService
} from '@app/services';
import { LMUniqueIdService } from '@app/utils';
import { LMAppComponentService } from '@utils/lm-app-component.service';
import { LMBannerHeaderComponentRef, LMFacadeModuleComponent, LMFacadeSearchResultsComponent, } from '@app/components';
import { LMFacadeService } from './services/lm-facade.service';
import { forkJoin } from 'rxjs';
import { tap } from "rxjs/operators";

@Component({
  selector:    'lm-facade-explorer',
  standalone: false,
  templateUrl: './lm-facade-explorer.component.html',
  styleUrl: './lm-facade-explorer.component.scss',
})
export class LMFacadeExplorerComponent extends LMBaseComponent {

  @ViewChild(forwardRef(() => LMFacadeModuleComponent)) facade: LMFacadeModuleComponent | undefined;
  @ViewChild(forwardRef(() => LMFacadeSearchResultsComponent)) search: LMFacadeSearchResultsComponent | undefined;

  showModuleFacadeBCComponent = signal(false);
  businessObjectOptions: LMFacadeBusinessObjectOptions | undefined;

  showSearchResultsPanel = signal(false);
  public searchTerms: LMFacadeSearchEvent | undefined;
  public searchResultsBusinessObjects: Array<LMSearchBusinessObject> = [];
  public searchResultsTemplates: Array<LMFacadeReportTemplate> = [];

  private facadesFetched = false;
  private templatesFetched = false;
  private defaultWidth = 'calc(100% - 300px)';
  moduleWidth = signal(this.defaultWidth);
  showModuleContent = signal(true);

  showWelcome = signal(true);

  state: LMFacadeExplorerState = { selectedCategory: '_start_', hideWelcome: false, view: 'tiles' };
  selectedModule: LMModuleFacade | undefined;
  moduleFacades: Array<LMModuleFacade> | undefined;
  templates: Array<LMFacadeReportTemplate> = [];
  suggestedTemplates: Array<LMFacadeReportTemplate> = [];
  suggestedBusinessObjects: Array<LMBusinessObject> | undefined;

  private startFromScratchBO: LMBusinessObject = {
    moduleTag: '_start_',
    category: '',
    businessClass: '',
    title: Soho.Locale.translate('Facade_StartFromScratchTitle'),
    description: '',
  };

  constructor(
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected lmComponentInfoService: LMComponentInfoService,
    protected lmUniqueIdService: LMUniqueIdService,
    protected lmAppComponentService: LMAppComponentService,
    protected lmModalWrapperService: LMModalWrapperService,
    protected lmModalOpenService: LMModalOpenService,
    protected lmBannerHeaderCompRef: LMBannerHeaderComponentRef,
    protected lmReportFacadeService: LMFacadeService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected lmContextService: LMContextService,
    private currentComponentService: LMRouteComponentService,
  ) {
    super(lmBusyIndicatorService, lmComponentInfoService, lmUniqueIdService, lmAppComponentService, lmModalWrapperService, lmModalOpenService);

    this.lmContextService.setClearNextPageState(false);
    this.lmBannerHeaderCompRef.instance.toolbarSectionTitle = Soho.Locale.translate("Facade_ExplorerTitle");
    this.lmBusyIndicatorService.showBusyIndicator();
    this.currentComponentService.setRouteComponent(this as Component);

    forkJoin({
      facades: this.lmReportFacadeService.getModuleFacades().pipe(
          tap(facades => this.handleGetModuleFacade(facades))
      ),
      templates: this.lmReportFacadeService.getTemplates().pipe(
          tap(templates => this.handleGetTemplates(templates))
      ),
    }).subscribe({
      complete: ()    => { this.hideBusyIndicator(); },
      error: (error)  => { this.handleError(error);  },
    });
  }

  getComponentInfo(additionInfo?: LMCompInfoAdditionalInfo): LMComponentInfo | undefined {
    return undefined;
  }

  getComponentPlaceHolder(): ViewContainerRef | undefined {
    return undefined;
  }

  getSpec(): LMListSpec | LMFormSpec | LMPageSpec | undefined {
    return undefined;
  }

  getUserWebRequestId(): string {
    return 'facadeexplorer';
  }

  isRouteReuseActive(): boolean {
    return false;
  }

  setRouteReuseActive(active: boolean, keys?: string): void {
      if (active) {
          this.lmBusyIndicatorService.hideBusyIndicator();
      }
  }

  saveComponentState(includeSubComponents?: boolean) {
    // nothing to save
  }

  moduleSelected(event: LMModuleFacadeSelectedEvent) {
    this.selectedModule = event.moduleFacade;
    this.state.selectedCategory = event.moduleFacade.module;
    this.reset();
  }

  startedSelected(event: LMFacadeEvent) {
    this.selectedModule = undefined;
    this.state.selectedCategory = '_start_';
    this.reset();
  }

  private reset() {
    if (this.facade)
      this.facade.closeFieldsPanel();
    if (this.search && this.showSearchResultsPanel()) {
      this.search.closeFieldsPanel();
      this.showSearchResultsPanel.set(false);
    }
  }

  private handleGetModuleFacade(facades: Array<LMModuleFacade> | undefined) {
    this.facadesFetched = true;

    if (this.facadesFetched && this.templatesFetched) {
      this.hideBusyIndicator();
    }
    const unsortedFacades:Array<LMModuleFacade> = facades === undefined ? [] : facades;
    unsortedFacades.sort((a, b) => {
      if (a.title < b.title) {
        return -1;
      }
      if (a.title > b.title) {
        return 1;
      }
      return 0;
    });
    this.moduleFacades = unsortedFacades;
  }

  handleSearch(event: LMFacadeSearchEvent) {
    this.searchTerms = event;
    this.searchResultsBusinessObjects = [];
    this.searchResultsTemplates = [];
    this.moduleWidth.set(this.defaultWidth);
    if (this.searchTerms.value.length > 0) {
      this.showSearchResultsPanel.set(true);
      this.searchBusinessObjects();
      this.searchTemplates();
      this.showWelcome.set(false);
    } else {
      this.showSearchResultsPanel.set(false);
      this.showWelcome.set(true);
      this.reset();
    }
  }

  openStartFromScratch(businessClass: string) {
    this.lmBusyIndicatorService.showBusyIndicator();
    this.lmReportFacadeService.getBusinessObject(businessClass)
        .subscribe({
          next:  (v) => {  this.openModuleFacadeBCComponent(v); },
          error: (e) => { this.handleError(e);                  }
        });
  }

  openModuleFacadeBCComponent(businessObject: LMBusinessObject) {
    this.lmBusyIndicatorService.showBusyIndicator();
    const facadeFetch = this.lmReportFacadeService.getBusinessClassFacade(businessObject.businessClass);
    const allListsFetch = this.lmReportFacadeService.getAllLists(businessObject.businessClass);

    forkJoin([ facadeFetch, allListsFetch ])
        .subscribe({
          next: (v)       => { this.handleOpenModuleFacadeBCComponent(businessObject, v[0], v[1]); },
          error: (e)                                 => { this.handleError(e);                                                }
        });
  }

  toggleTemplateDetails(showTemplate: boolean): void {
    if (showTemplate)
      this.moduleWidth.set('calc(100% - 600px');
    else
      this.moduleWidth.set(this.defaultWidth);
  }

  private handleOpenModuleFacadeBCComponent(businessObject: LMBusinessObject, businessClassFacade: LMBusinessClassFacade, allLists:Array<LMFacadeReportTemplate>) {
    this.lmBusyIndicatorService.hideBusyIndicator();
    let templates: Array<LMFacadeReportTemplate> = [];
    templates = this.templates.filter((template: LMFacadeReportTemplate) => businessObject.businessClass === template.businessView);

    this.businessObjectOptions = {
      businessObject: businessObject,
      businessClassFacade: businessClassFacade,
      templates: templates,
      listTemplates: allLists ? allLists : []
    };

    this.showModuleFacadeBCComponent.set(true);
    this.showModuleContent.set(false);

  }

  closeModuleFacadeBCComponent(close: boolean) {
    if (close) {
      this.showModuleFacadeBCComponent.set(false);
      this.showModuleContent.set(true);
      this.businessObjectOptions = undefined;
    }
  }

  private handleGetTemplates(suggestions: LMFacadeSuggestionResponse) {
    this.templatesFetched = true;

    if (this.facadesFetched && this.templatesFetched) {
      this.hideBusyIndicator();
    }
    this.suggestedTemplates = suggestions === undefined ? [] : suggestions.suggestedTemplates;
    this.templates = suggestions === undefined ? [] : suggestions.templates;
    this.suggestedBusinessObjects = suggestions === undefined ? [] : suggestions.suggestedBusinessObjects;
    this.addStartFromScratch();
    this.lmBusyIndicatorService.hideBusyIndicator();
  }

  private handleError(error: any) {
    this.lmBusyIndicatorService.hideBusyIndicator();
    this.lmErrorUtilService.handleUnexpectedError(error);
  }

  private searchBusinessObjects(): void {
    if (this.searchTerms && this.moduleFacades) {
      this.searchResultsBusinessObjects = this.lmReportFacadeService.searchBusinessObjects(this.searchTerms, this.moduleFacades);
    }
  }

  private searchTemplates(): void {
    if (this.searchTerms) {
      this.searchResultsTemplates = this.lmReportFacadeService.searchTemplates(this.searchTerms, this.templates);
    }
  }

  private addStartFromScratch() {
    if (this.suggestedBusinessObjects) {
      this.suggestedBusinessObjects.push(this.startFromScratchBO);
    }
  }
}
